import React, { useState, useRef, useEffect } from 'react'
import { File, Edit, Download, Share2, Trash2, MoreVertical, Loader, ArrowDownAZ, ArrowUpZA, Calendar, FileType, HardDrive } from 'lucide-react'
import { getFriendlyFileType } from '../utils/fileHelpers'
import { useLocalStorage } from '../hooks/useLocalStorage'

interface FileItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  modifiedAt: string
  path: string
}

interface FileGridProps {
  files: FileItem[]
  selectedItems: string[]
  onItemClick: (item: FileItem) => void
  onItemSelect: (itemId: string) => void
  onRenameItem: (item: FileItem) => void
  onCreateShareLink: (item: FileItem) => void
  onDeleteItem: (item: FileItem) => void
  sharingItemId?: string
  loading: boolean
}

type SortField = 'name' | 'type' | 'size' | 'modifiedAt'
type SortDirection = 'asc' | 'desc'

const FileGrid: React.FC<FileGridProps> = ({
  files,
  selectedItems,
  onItemClick,
  onItemSelect,
  onRenameItem,
  onCreateShareLink,
  onDeleteItem,
  sharingItemId,
  loading
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [showSortMenu, setShowSortMenu] = useState(false)
  const sortMenuRef = useRef<HTMLDivElement | null>(null)
  
  // Sorting state with localStorage persistence
  const [sortField, setSortField] = useLocalStorage<SortField>('fileGridSortField', 'name')
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('fileGridSortDirection', 'asc')
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle dropdown menus
      if (activeDropdown && dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown]?.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
      
      // Handle sort menu
      if (showSortMenu && sortMenuRef.current && 
          !sortMenuRef.current.contains(event.target as Node)) {
        setShowSortMenu(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdown, showSortMenu])
  
  const toggleDropdown = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation()
    setActiveDropdown(activeDropdown === itemId ? null : itemId)
  }
  
  // Handle sorting
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field and default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
    setShowSortMenu(false)
  }
  
  // Sort the files
  const sortedFiles = [...files].sort((a, b) => {
    // Always put folders first, then sort by the selected field
    if (a.type === 'folder' && b.type !== 'folder') return -1
    if (a.type !== 'folder' && b.type === 'folder') return 1
    
    // Sort by the selected field
    let comparison = 0
    
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name)
        break
      case 'type':
        if (a.type === b.type) {
          // If both are files, compare by mime type
          if (a.type === 'file' && b.type === 'file') {
            const aType = a.mimeType || ''
            const bType = b.mimeType || ''
            comparison = aType.localeCompare(bType)
          } else {
            // If both are folders, sort by name
            comparison = a.name.localeCompare(b.name)
          }
        }
        break
      case 'size':
        // For folders or undefined sizes, treat as 0
        const aSize = a.size || 0
        const bSize = b.size || 0
        comparison = aSize - bSize
        break
      case 'modifiedAt':
        comparison = new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime()
        break
    }
    
    // Apply sort direction
    return sortDirection === 'asc' ? comparison : -comparison
  })
  
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--color-primary)] mx-auto mb-4"></div>
        <p>Loading files...</p>
      </div>
    )
  }
  
  if (files.length === 0) {
    return (
      <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-8 text-center">
        <p className="text-[var(--color-accent)] mb-2">No files found</p>
        <p>This folder is empty. Upload files or create a new folder.</p>
      </div>
    )
  }
  
  // Custom folder icon with solid fill
  const FolderIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="48" 
      height="48" 
      viewBox="0 0 24 24" 
      fill="var(--color-secondary)" 
      stroke="var(--color-secondary)" 
      strokeWidth="0.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  )
  
  // Get sort icon
  const getSortIcon = () => {
    if (sortDirection === 'asc') {
      return <ArrowDownAZ size={18} />
    } else {
      return <ArrowUpZA size={18} />
    }
  }
  
  // Get sort field icon
  const getSortFieldIcon = () => {
    switch (sortField) {
      case 'name':
        return <ArrowDownAZ size={16} className="mr-2" />
      case 'type':
        return <FileType size={16} className="mr-2" />
      case 'size':
        return <HardDrive size={16} className="mr-2" />
      case 'modifiedAt':
        return <Calendar size={16} className="mr-2" />
    }
  }
  
  // Get sort field name
  const getSortFieldName = () => {
    switch (sortField) {
      case 'name':
        return 'Name'
      case 'type':
        return 'Type'
      case 'size':
        return 'Size'
      case 'modifiedAt':
        return 'Date Modified'
    }
  }
  
  return (
    <div>
      {/* Sort Controls */}
      <div className="flex justify-end mb-4 relative">
        <button
          className="btn btn-outline flex items-center text-sm py-1.5 px-3"
          onClick={() => setShowSortMenu(!showSortMenu)}
        >
          {getSortIcon()}
          <span className="ml-2">Sort by: {getSortFieldName()}</span>
        </button>
        
        {showSortMenu && (
          <div 
            ref={sortMenuRef}
            className="absolute right-0 top-10 w-48 bg-white dark:bg-[#2a2a2a] shadow-custom-medium dark:shadow-lg dark:shadow-black/30 rounded-md z-50 border border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30"
          >
            <div className="py-1">
              <button
                className={`w-full text-left px-4 py-2 flex items-center ${
                  sortField === 'name' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                }`}
                onClick={() => handleSort('name')}
              >
                <ArrowDownAZ size={16} className="mr-2" />
                Name
              </button>
              <button
                className={`w-full text-left px-4 py-2 flex items-center ${
                  sortField === 'type' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                }`}
                onClick={() => handleSort('type')}
              >
                <FileType size={16} className="mr-2" />
                Type
              </button>
              <button
                className={`w-full text-left px-4 py-2 flex items-center ${
                  sortField === 'size' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                }`}
                onClick={() => handleSort('size')}
              >
                <HardDrive size={16} className="mr-2" />
                Size
              </button>
              <button
                className={`w-full text-left px-4 py-2 flex items-center ${
                  sortField === 'modifiedAt' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                }`}
                onClick={() => handleSort('modifiedAt')}
              >
                <Calendar size={16} className="mr-2" />
                Date Modified
              </button>
            </div>
            <div className="border-t border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30 py-1">
              <button
                className={`w-full text-left px-4 py-2 flex items-center ${
                  sortDirection === 'asc' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                }`}
                onClick={() => setSortDirection('asc')}
              >
                <ArrowDownAZ size={16} className="mr-2" />
                Ascending
              </button>
              <button
                className={`w-full text-left px-4 py-2 flex items-center ${
                  sortDirection === 'desc' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                }`}
                onClick={() => setSortDirection('desc')}
              >
                <ArrowUpZA size={16} className="mr-2" />
                Descending
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* File Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-12">
        {sortedFiles.map((file) => (
          <div
            key={file.id}
            className={`bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light dark:shadow-lg dark:shadow-black/20 p-4 cursor-pointer transition-all duration-300 hover:shadow-custom-medium ${
              selectedItems.includes(file.id) 
                ? 'ring-2 ring-[var(--color-secondary)]' 
                : 'hover:border-[var(--color-secondary)] dark:hover:border-[var(--color-secondary)]'
            }`}
            onClick={() => onItemClick(file)}
            onMouseEnter={() => setHoveredItem(file.id)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="flex flex-col">
              {/* File/Folder Icon with Checkbox Overlay */}
              <div className="relative mb-3">
                <div className="aspect-square flex items-center justify-center bg-[var(--color-background)] dark:bg-[#1f1f1f] rounded-md border border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30">
                  {file.type === 'folder' ? (
                    <FolderIcon />
                  ) : (
                    <File size={48} className="text-[var(--color-primary)]" />
                  )}
                </div>
                
                {/* Checkbox positioned in the top-right corner of the icon - only visible on hover or when selected */}
                <div 
                  className={`absolute top-2 right-2 z-10 transition-opacity duration-200 ${
                    hoveredItem === file.id || selectedItems.includes(file.id) ? 'opacity-100' : 'opacity-0'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation() // Prevent triggering the parent onClick
                    onItemSelect(file.id)
                  }}
                >
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-[var(--color-accent)] dark:border-[var(--color-secondary)] dark:bg-[#1f1f1f] dark:checked:bg-[var(--color-secondary)] cursor-pointer"
                    checked={selectedItems.includes(file.id)}
                    onChange={(e) => e.stopPropagation()} // Prevent bubbling
                  />
                </div>
              </div>
              
              {/* File/Folder Info */}
              <div className="flex-grow">
                <p className="font-medium truncate text-[var(--color-primary)] dark:text-[var(--color-primary)]" title={file.name}>
                  {file.name}
                </p>
                <p className="text-sm text-[var(--color-accent)] dark:text-[var(--color-accent)] opacity-90">
                  {file.type === 'file' && file.mimeType 
                    ? getFriendlyFileType(file.mimeType)
                    : file.type === 'folder'
                    ? 'Folder'
                    : ''}
                </p>
                <p className="text-sm text-[var(--color-accent)] dark:text-[var(--color-accent)] opacity-90">
                  {file.type === 'file' && file.size !== undefined
                    ? `${(file.size / 1024).toFixed(2)} KB`
                    : ''}
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="mt-2 flex justify-between items-center border-t border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-20 pt-2">
              <div className="flex space-x-1">
                <button
                  className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRenameItem(file)
                  }}
                  title="Rename"
                >
                  <Edit size={16} />
                </button>
                {file.type === 'file' && (
                  <button
                    className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                    onClick={(e) => {
                      e.stopPropagation()
                      console.log('Download file:', file.name)
                    }}
                    title="Download"
                  >
                    <Download size={16} />
                  </button>
                )}
                <button
                  className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                  onClick={(e) => {
                    e.stopPropagation()
                    onCreateShareLink(file)
                  }}
                  title="Share"
                  disabled={sharingItemId === file.id}
                >
                  {sharingItemId === file.id ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <Share2 size={16} />
                  )}
                </button>
              </div>
              
              <div className="relative" style={{ overflow: 'visible' }}>
                <button
                  className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                  onClick={(e) => toggleDropdown(e, file.id)}
                  title="More options"
                >
                  <MoreVertical size={16} />
                </button>
                
                {activeDropdown === file.id && (
                  <div 
                    ref={(el) => dropdownRefs.current[file.id] = el}
                    className="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-[#2a2a2a] shadow-custom-medium dark:shadow-lg dark:shadow-black/30 rounded-md z-50 border border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30"
                    onClick={(e) => e.stopPropagation()}
                    style={{ overflow: 'visible' }}
                  >
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)] flex items-center"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRenameItem(file)
                        setActiveDropdown(null)
                      }}
                    >
                      <Edit size={16} className="mr-2" />
                      Rename
                    </button>
                    {file.type === 'file' && (
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)] flex items-center"
                        onClick={(e) => {
                          e.stopPropagation()
                          console.log('Download file:', file.name)
                          setActiveDropdown(null)
                        }}
                      >
                        <Download size={16} className="mr-2" />
                        Download
                      </button>
                    )}
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] text-[var(--color-primary)] flex items-center"
                      onClick={(e) => {
                        e.stopPropagation()
                        onCreateShareLink(file)
                        setActiveDropdown(null)
                      }}
                      disabled={sharingItemId === file.id}
                    >
                      {sharingItemId === file.id ? (
                        <>
                          <Loader size={16} className="mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Share2 size={16} className="mr-2" />
                          Share
                        </>
                      )}
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteItem(file)
                        setActiveDropdown(null)
                      }}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Move to Trash
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default FileGrid